import { IconEdit, IconEye, IconKey, IconKeyOff, IconTrash } from "@tabler/icons-react";
import { userApi } from "apis";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomTable from "components/ui/CustomTable";
import CustomText from "components/ui/CustomText/CustomText";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import { STATUS } from "constants/common/common";
import { CommonParamsType } from "constants/types/common";
import { UserResponse } from "constants/types/user";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "mantine-react-table";
import { FORM_IDS } from "pages/User/config";
import { FC, useMemo, useRef, useCallback } from "react";
import { useNotification, useUsers } from "utils/hooks";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { getRolePermissionVi } from "utils/validation/string";
import EditForm from "../EditForm/EditForm";
import { useSWRConfig } from "swr";
import { getUserListKey } from "utils/hooks/request/useUsers";
import { Loading } from "components";
import ResetPasswordForm from "../ResetPasswordForm/ResetPasswordForm";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";

interface UserTableProps {
  params: CommonParamsType;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const ICON_SIZE = 18;

const UserTable: FC<UserTableProps> = ({
  params,
  onPageChange,
  onLimitChange,
}) => {
  const { notification } = useNotification();
  const { mutate } = useSWRConfig();
  const ability = useAbility();

  const {
    users,
    pagination,
    isLoading,
    mutate: localMutate,
  } = useUsers(params);

  const { openCrudModal, closeAll, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);

  const handleDeleteUser = useCallback(
    async (userId: string) => {
      try {
        await userApi.deleteUser(userId);
        notification.success({ title: "Xóa người dùng thành công" });
        closeAll();
        localMutate();
        onPageChange(1);
      } catch (error) {
        notification.error({ title: "Xóa người dùng thất bại" });
      }
    },
    [notification, closeAll, localMutate, onPageChange],
  );

  const handleOpenDeleteModal = useCallback(
    (userId: string) => {
      openCrudModal(
        <CustomText variant="section-title">
          Xác nhận xóa người dùng
        </CustomText>,
        {
          mode: "delete",
          formId: FORM_IDS.DELETE,
          onConfirmDelete: async () => await handleDeleteUser(userId),
        },
      );
    },
    [openCrudModal, handleDeleteUser],
  );

  const handleCloseModal = useCallback(() => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  }, [closeCrudModal]);

  const handleOpenEditModal = useCallback(
    (user: UserResponse) => {
      const modalId = openCrudModal(
        <CustomText variant="section-title">Chỉnh sửa người dùng</CustomText>,
        {
          mode: "edit",
          formId: FORM_IDS.EDIT,
          children: (
            <EditForm
              user={user}
              mutate={() => mutate(getUserListKey(params))}
              onClose={() => handleCloseModal()}
            />
          ),
          submitText: "Cập nhật",
        },
        750,
      );
      activeModalId.current = modalId;
    },
    [openCrudModal, mutate, params, handleCloseModal],
  );

  const handleOpenResetPasswordModal = useCallback(
    (user: UserResponse) => {
      const modalId = openCrudModal(
        <CustomText variant="section-title">Đặt lại mật khẩu</CustomText>,
        {
          mode: "edit",
          formId: FORM_IDS.RESET_PASSWORD,
          children: (
            <ResetPasswordForm
              user={user}
              mutate={() => mutate(getUserListKey(params))}
              onClose={() => handleCloseModal()}
            />
          ),
          submitText: "Xác nhận",
        },
        500,
      );
      activeModalId.current = modalId;
    },
    [openCrudModal, mutate, params, handleCloseModal],
  );

  const handleActiveUser = useCallback(
    async (userId: string) => {
      try {
        await userApi.deactiveUser(userId);
        localMutate();
        notification.success({ title: "Khóa tài khoản thành công" });
      } catch (error: any) {
        notification.error({ title: error?.error?.message ?? "Có lỗi xảy ra" });
      }
    },
    [localMutate],
  );

  const userActions: ActionItem<UserResponse>[] = useMemo(
    () => [
      {
        key: "view",
        label: "Xem chi tiết",
        icon: <IconEye size={ICON_SIZE} />,
        color: "blue",
        permission: "user:read",
        onClick: (row) => {
          console.log("Xem chi tiết user:", row.username);
          // navigate(`/users/${row.id}`)
        },
      },
      {
        key: "edit",
        label: "Chỉnh sửa",
        icon: <IconEdit size={ICON_SIZE} />,
        color: "orange",
        permission: "user:update",
        onClick: (row) => {
          handleOpenEditModal(row);
        },
      },
      {
        key: "delete",
        label: "Xóa",
        icon: <IconTrash size={ICON_SIZE} />,
        color: "red",
        permission: "user:delete",
        onClick: (row) => {
          handleOpenDeleteModal(row.id);
        },
      },
      {
        key: "reset-password",
        label: "Đặt lại mật khẩu",
        icon: <IconKey size={ICON_SIZE} />,
        color: "blue",
        permission: "user:update",
        onClick: (row) => handleOpenResetPasswordModal(row),
      },
      {
        key: "deactive",
        label: "Khóa tài khoản",
        icon: <IconKeyOff size={ICON_SIZE} />,
        color: "yellow",
        permission: "user:update",
        onClick: (row) => handleActiveUser(row.id),
      },
    ],
    [
      handleOpenEditModal,
      handleOpenDeleteModal,
      handleOpenResetPasswordModal,
      handleActiveUser,
    ],
  );

  const hasActions = useMemo(
    () => userActions.some((a) => canAccessRequirement(ability, a.permission, true)),
    [userActions, ability],
  );

  const columns: MRT_ColumnDef<UserResponse>[] = [
    {
      id: "stt",
      header: "STT",
      size: 5,
      grow: false,
      Cell: ({ row }) => row.index + 1,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "username",
      header: "Tên đăng nhập",
    },
    {
      accessorKey: "fullname",
      header: "Họ và tên",
    },
    {
      accessorKey: "roles",
      header: "Quyền hạn",
      Cell: ({ cell }) =>
        cell
          .getValue<string[]>()
          .map((value) => getRolePermissionVi(value))
          .join(","),
    },
    {
      accessorKey: "isActive",
      header: "Trạng thái",
      // size: 130,
      grow: false,
      Cell: ({ cell }) =>
        cell.getValue() ? (
          <CustomBadge label={STATUS.ACTIVE_LABEL} color="green" />
        ) : (
          <CustomBadge label={STATUS.INACTIVE_LABEL} color="gray" />
        ),
    },
    ...(hasActions
      ? [
          {
            id: "actions",
            header: "Thao tác",
            enableSorting: false,
            enableColumnFilter: false,
            size: 50,
            grow: false,
            enableResizing: false,
            mantineTableHeadCellProps: { align: "center" },
            mantineTableBodyCellProps: { align: "center" },
            Cell: ({ row }: any) => (
              <GroupActions row={row.original} actions={userActions} />
            ),
          } satisfies MRT_ColumnDef<UserResponse>,
        ]
      : []),
  ];

  const handleChange = useCallback(
    (state: {
      page: number;
      limit: number;
      filters?: MRT_ColumnFiltersState;
    }) => {
      if (pagination && state.page !== pagination.page) {
        onPageChange(state.page);
      } else if (pagination && state.limit !== pagination.limit) {
        onLimitChange(state.limit);
      }
      // Xử lý filters nếu có (ví dụ: chuyển đổi filters thành search text)
    },
    [onPageChange, onLimitChange, pagination],
  );

  return (
    <Loading variant="overlay" visible={isLoading}>
      <CustomTable
        columns={columns}
        data={users ?? []}
        pagination={pagination!}
        onStateChange={handleChange}
      />
    </Loading>
  );
};

export default UserTable;
